import React, {useEffect, useState} from 'react';
import {useParams} from 'react-router-dom';
import {
    Container,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    CircularProgress,
    Alert,
    Box,
} from '@mui/material';
import {getLectureAnalytics} from '../services/api';

type StatsValue = {
    min: number;
    max: number;
    mean: number;
    median: number;
};

type StatsData = {
    tasks: Record<'total' | 'manual' | 'rag', number>;
    time: Record<'total' | 'manual' | 'rag', number>;
    'time/task': Record<'total' | 'manual' | 'rag', StatsValue>;
    'n_comments': Record<'total' | 'manual' | 'rag', number>;
    'comments/task': Record<'total' | 'manual' | 'rag', StatsValue>;
};

type RawStats = {
    notebook_id: number[];
    reviewer: string[];
    time: number[]; // в мс
    n_comments: number[];
    rag_status: string[];
};

const StatsTable: React.FC<{ title: string; stats: StatsData }> = ({ title, stats }) => {
    const types: ('total' | 'manual' | 'rag')[] = ['total', 'manual', 'rag'];
    const metrics = ['min', 'max', 'mean', 'median'];

    const renderDataCell = (value: number | undefined, isTime: boolean = false) =>
        value === -1 || value === undefined
            ? '-'
            : isTime
                ? (value / 60000).toFixed(1)
                : value;

    const renderMetricCells = (obj: StatsValue, typeIdx: number, isTime: boolean = false) =>
        metrics.map((metric, i) => (
            <TableCell
                key={`${typeIdx}-${metric}`}
                align="center"
                sx={{
                    fontSize: 13,
                    px: 1,
                    ...(i === 0 && typeIdx > 0 ? { borderLeft: '2px solid #ddd' } : {}),
                }}
            >
                {renderDataCell(obj[metric as keyof StatsValue], isTime)}
            </TableCell>
        ));

    return (
        <>
            <Typography variant="h6" sx={{ mt: 4, mb: 1 }}>
                {title}
            </Typography>
            <TableContainer component={Paper}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell
                                rowSpan={2}
                                sx={{
                                    borderRight: '2px solid #ddd',
                                    borderTop: '2px solid #ddd',
                                    backgroundColor: '#fafafa',
                                    fontWeight: 600,
                                }}
                            >
                                Показатель
                            </TableCell>
                            {types.map((type, i) => (
                                <TableCell
                                    key={type}
                                    align="center"
                                    colSpan={4}
                                    sx={{
                                        ...(i > 0 ? { borderLeft: '2px solid #ddd' } : {}),
                                        borderTop: '2px solid #ddd',
                                        backgroundColor: '#fafafa',
                                        fontWeight: 600,
                                    }}
                                >
                                    {type}
                                </TableCell>
                            ))}
                        </TableRow>
                        <TableRow>
                            {types.flatMap((_, typeIdx) =>
                                metrics.map((metric, i) => (
                                    <TableCell
                                        key={`${typeIdx}-${metric}`}
                                        align="center"
                                        sx={i === 0 && typeIdx > 0 ? { borderLeft: '2px solid #ddd' } : {}}
                                    >
                                        {metric}
                                    </TableCell>
                                ))
                            )}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {[
                            { key: 'tasks', label: 'tasks', isTime: false },
                            { key: 'time', label: 'time (min)', isTime: true },
                        ].map(({ key, label, isTime }) => (
                            <TableRow key={label} sx={{ bgcolor: 'background.default' }}>
                                <TableCell
                                    sx={{
                                        borderRight: '2px solid #ddd',
                                        backgroundColor: '#fafafa',
                                        fontWeight: 500,
                                    }}
                                >
                                    {label}
                                </TableCell>
                                {types.map((type, i) => (
                                    <TableCell
                                        key={`${label}-${type}`}
                                        align="center"
                                        colSpan={4}
                                        sx={{
                                            fontSize: 13,
                                            px: 1,
                                            ...(i > 0 ? { borderLeft: '2px solid #ddd' } : {}),
                                        }}
                                    >
                                        {renderDataCell((stats as any)[key][type], isTime)}
                                    </TableCell>
                                ))}
                            </TableRow>
                        ))}

                        {[
                            { key: 'time/task', label: 'time/task (min)', isTime: true },
                            { key: 'n_comments', label: 'n_comments', isTime: false },
                            { key: 'comments/task', label: 'comments/task', isTime: false },
                        ].map(({ key, label, isTime }) => (
                            <TableRow key={label}>
                                <TableCell
                                    sx={{
                                        borderRight: '2px solid #ddd',
                                        backgroundColor: '#fafafa',
                                        fontWeight: 500,
                                    }}
                                >
                                    {label}
                                </TableCell>
                                {types.map((type, typeIdx) => {
                                    const value = stats[key as keyof StatsData][type];
                                    if (typeof value === 'number') {
                                        return (
                                            <TableCell
                                                key={`${label}-${type}`}
                                                align="center"
                                                colSpan={4}
                                                sx={{
                                                    fontSize: 13,
                                                    px: 1,
                                                    ...(typeIdx > 0 ? { borderLeft: '2px solid #ddd' } : {}),
                                                }}
                                            >
                                                {renderDataCell(value, isTime)}
                                            </TableCell>
                                        );
                                    } else {
                                        return renderMetricCells(value, typeIdx, isTime);
                                    }
                                })}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </>
    );
};

const RawStatsTable: React.FC<{ data: RawStats }> = ({ data }) => {
    return (
        <>
            <Typography variant="h6" sx={{ mt: 4, mb: 1 }}>
                Raw Stats
            </Typography>
            <TableContainer component={Paper}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>ID ноутбука</TableCell>
                            <TableCell>Ревьюер</TableCell>
                            <TableCell>Время (мин)</TableCell>
                            <TableCell>Количество комментариев</TableCell>
                            <TableCell>Статус RAG</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {data.notebook_id.map((id, idx) => (
                            <TableRow key={`${id}-${idx}`}>
                                <TableCell>{id}</TableCell>
                                <TableCell>{data.reviewer[idx]}</TableCell>
                                <TableCell>{(data.time[idx] / 60000).toFixed(1)}</TableCell>
                                <TableCell>{data.n_comments[idx]}</TableCell>
                                <TableCell>{data.rag_status[idx]}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </>
    );
};

export const LectureAnalyticsPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const lectureId = id;
    const [stats, setStats] = useState<StatsData | null>(null);
    const [statsWoTime, setStatsWoTime] = useState<StatsData | null>(null);
    const [rawStats, setRawStats] = useState<RawStats | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [lectureName, setLecName] = useState<string | null>(null);

    useEffect(() => {
        if (!lectureId) return;

        const fetchData = async () => {
            try {
                const data: any = await getLectureAnalytics(Number(lectureId));
                setStats(data.stats_with_check_time);
                setStatsWoTime(data.stats_wo_check_time);
                setLecName(data.lecture_name);
                setRawStats(data.raw_stats);
            } catch (err: any) {
                setError(err.message || 'Ошибка загрузки аналитики');
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [lectureId]);

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            <Typography variant="h4" gutterBottom>
                {lectureName}
            </Typography>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                    <CircularProgress />
                </Box>
            ) : error ? (
                <Alert severity="error">{error}</Alert>
            ) : (
                <>
                    {stats && <StatsTable title="С отсечкой времени" stats={stats} />}
                    {statsWoTime && <StatsTable title="Без отсечки времени" stats={statsWoTime} />}
                    {rawStats && <RawStatsTable data={rawStats} />}
                </>
            )}
        </Container>
    );
};
